import React, { useState } from 'react';
import PropTypes from 'prop-types';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';
import PortalDropdown, { DropdownMenuItem } from 'views_shared/app/javascript/components/PortalDropdown';

/**
 * InboxDropdownMenu - Three-dot menu for inbox actions
 *
 * Actions:
 * - Manage Conversations (toggle selection mode)
 * - Mark All as Read
 *
 * On the Trash tab, both actions are disabled.
 */
function InboxDropdownMenu({
 onToggleSelectionMode, onMarkAllReadComplete, selectionMode, trashTabActive = false,
}) {
  // Only mention what was actually marked — a promoter (or an all-read inbox) must not get
  // a toast about pitches. Falls back to a neutral line if the server sent no counts
  // (deploy skew: older server, newer client).
  const markAllToastBody = (data) => {
    const convs = data?.conversations_marked;
    const pitches = data?.pitches_marked;
    if (typeof convs !== 'number' || typeof pitches !== 'number') return 'Everything marked as read';
    const parts = [];
    if (convs > 0) parts.push(`${convs} conversation${convs === 1 ? '' : 's'}`);
    if (pitches > 0) parts.push(`${pitches} pitch${pitches === 1 ? '' : 'es'}`);
    if (parts.length === 0) return 'Everything was already read';
    return `Marked ${parts.join(' and ')} as read`;
  };

  // The menu closes on click, so in-flight feedback lives in a toast — without it a big
  // inbox looks like nothing happened for several seconds (staging feedback, 2026-09-02).
  const [marking, setMarking] = useState(false);
  const handleMarkAllRead = async () => {
    if (marking) return;
    setMarking(true);
    globalThis.Qwoted.showToast({
      toastTitle: 'Marking all as read…',
      toastBody: 'This can take a few seconds for a large inbox.',
      variant: 'info',
    });
    try {
      const result = await qapi.conversations.markAllRead();
      if (result.error) {
        throw new Error(result.error.statusText || 'Failed to mark all as read');
      }

      globalThis.Qwoted.showToast({
        toastTitle: 'Success',
        toastBody: markAllToastBody(result.data),
        variant: 'success',
      });

      onMarkAllReadComplete?.();
    } catch (error) {
      qlog.error('Failed to mark all as read:', error);

      globalThis.Qwoted.showToast({
        toastTitle: 'Error',
        toastBody: 'Failed to mark conversations as read. Please try again.',
        variant: 'danger',
      });
    } finally {
      setMarking(false);
    }
  };

  return (
    <PortalDropdown toggleClassName="btn btn-secondary btn-icon rounded-2 text-neutral-80 fs-5 border-0 shadow-none">
      <DropdownMenuItem
        icon="fa-regular fa-list-check"
        label="Manage Conversations"
        onClick={onToggleSelectionMode}
        active={selectionMode}
        disabled={trashTabActive}
      />
      <DropdownMenuItem
        icon="fa-regular fa-message-check"
        label={marking ? 'Marking all as read…' : 'Mark All as Read'}
        onClick={handleMarkAllRead}
        disabled={trashTabActive || marking}
      />
    </PortalDropdown>
  );
}

InboxDropdownMenu.propTypes = {
  onToggleSelectionMode: PropTypes.func.isRequired,
  onMarkAllReadComplete: PropTypes.func,
  selectionMode: PropTypes.bool,
  trashTabActive: PropTypes.bool,
};

export default InboxDropdownMenu;
